<template>
    <form
        action=""
        @submit.prevent="handleSubmit"
    >
        <div
            class="row"
            v-show="canReview"
        >
            <div class="col-12">
                <label>Employee:</label>
                <user-select
                    v-model="form.user_id"
                    :disabled="!isUserSpecific || !canEdit || saving"
                />
                <Error
                    :errors="errors"
                    name="user_id"
                />
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <label>Event Type:</label>
                <select
                    v-model="form.type"
                    class="form-control"
                    :disabled="!canEdit || saving"
                >
                    <option value="Annual Leave">
                        Annual Leave
                    </option>
                    <option value="Sickness">
                        Sickness
                    </option>
                    <option
                        value="Company Closed"
                        :disabled="!isAdmin"
                    >
                        Company Closed
                    </option>
                    <option
                        value="Bank Holiday"
                        :disabled="!isAdmin"
                    >
                        Bank Holiday
                    </option>
                </select>
                <Error
                    :errors="errors"
                    name="type"
                />
            </div>
        </div>
        <div class="row">
            <div class="col-12 col-md-6">
                <label>Start Date:</label>
                <date-picker
                    v-model="form.start_at"
                    :disabled="!canEdit || saving"
                    :options="startDatePickerOptions"
                />
                <Error
                    :errors="errors"
                    name="start_at"
                />
            </div>
            <div class="col-12 col-md-6">
                <label>End Date:</label>
                <date-picker
                    v-model="form.end_at"
                    :options="endDatePickerOptions"
                    :disabled="!canEdit || saving"
                />
                <Error
                    :errors="errors"
                    name="end_at"
                />
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <label>Notes</label>
                <textarea
                    v-model="form.notes"
                    placeholder="Enter additional notes here..."
                    rows="4"
                    class="form-control"
                    :disabled="!canEdit || saving"
                />
                <Error
                    :errors="errors"
                    name="notes"
                />
            </div>
        </div>
        <div
            class="row"
            v-if="isUserSpecific"
        >
            <div class="col-12">
                <label>Status:</label>
                <select
                    v-model="form.status"
                    class="form-control"
                    :disabled="!canReview || saving"
                >
                    <option>
                        Pending
                    </option>
                    <option>
                        Approved
                    </option>
                    <option>
                        Declined
                    </option>
                </select>
                <Error
                    :errors="errors"
                    name="status"
                />
            </div>
        </div>
        <div
            v-if="form.reviewer && isUserSpecific"
            class="row mt-2"
        >
            <div class="col-12">
                <label>Reviewed By:</label>
                <p>{{ form.reviewer.name }} at {{ form.reviewed_at | datetime }}</p>
            </div>
        </div>
        <div
            class="row"
            style="margin-top: 1rem;"
        >
            <div
                class="col-12 flex"
                v-if="canEdit"
            >
                <button
                    class="btn btn-primary"
                    :disabled="saving"
                >
                    <span v-if="saving">Saving... <i class="fa fa-spinner fa-spin" /></span>
                    <span v-else>Save</span>
                </button>
                <button
                    class="btn btn-danger"
                    v-if="form.id"
                    type="button"
                    :disabled="saving"
                    @click="handleDeleteClick"
                >
                    Delete
                </button>
                <button
                    type="button"
                    class="btn btn-default"
                    :disabled="saving"
                    @click="$emit('cancel')"
                >
                    Cancel
                </button>
            </div>
        </div>
    </form>
</template>
<script>
import DatePicker from "../../DateTimePickerComponent";
import UserSelect from "../../UserSelectComponent";
import Error from "../../ErrorComponent.vue";
import CalendarEventsService from "../../../services/CalendarEventsService";
import Auth from "../../../services/AuthService";
import {isEventEditable, isDateOnlyEventType, normalizeDateOnlyRange} from "../../../include/Calendar";

const initialForm = {
    type: "Annual Leave",
    status: "Pending",
};
export default {
    components: {
        DatePicker,
        UserSelect,
        Error,
    },
    props: {
        event: {
            type: Object,
            required: false,
            default: () => ({...initialForm}),
        },
    },
    data() {
        return {
            form: {...initialForm},
            errors: {},
            errorMessage: null,
            successMessage: null,
            saving: false,
        };
    },
    computed: {
        isUserSpecific() {
            return ['Annual Leave', 'Sickness'].includes(this.form.type);
        },
        isAdmin() {
            return Auth.user.admin_access;
        },
        canReview() {
            return this.isAdmin && this.isUserSpecific;
        },
        canEdit() {
            if (!isEventEditable(this.event)) {
                return false;
            }

            if (!this.event.id) {
                return true;
            }

            if (this.isAdmin) {
                return true;
            }

            return this.form.status === 'Pending'
                && this.form.user_id === Auth.user.id;
        },
        startDatePickerOptions() {
            return {format: 'L'};
        },
        endDatePickerOptions() {
            const options = {format: 'L'};
            if (this.form.start_at && window.moment.isMoment(this.form.start_at)) {
                options.minDate = this.form.start_at.clone().startOf('day');
            }
            return options;
        },
    },
    watch: {
        event: {
            handler(newEvent) {
                this.form = {
                    ...initialForm,
                    ...newEvent,
                    notes: newEvent.notes || newEvent.description,
                };
                this.ensureEndAtOnOrAfterStart(this.form.start_at);
                this.$forceUpdate();
            },
            immediate: true,
            deep: true,
        },
        'form.start_at'(start) {
            this.ensureEndAtOnOrAfterStart(start);
        },
        isUserSpecific: {
            handler() {
                if (!this.isUserSpecific) {
                    this.form.user_id = null;
                }
            },
            immediate: true,
        },
    },
    emits: ["saved", "cancel"],
    methods: {
        handleSubmit() {
            this.errors = {};
            if (this.saving) {
                return;
            }
            this.errorMessage = null;
            this.successMessage = null;
            this.saving = true;
            CalendarEventsService.saveEvent({
                ...this.form,
                ...this.normalizedDates(),
                description: this.form.notes || this.form.description,
            })
                .then(() => {
                    window.alert("Event saved successfully");
                    this.$emit("saved", {
                        event: this.form,
                    });
                    this.resetForm();
                    if (this.$refs.calendar) {
                        this.$refs.calendar.refresh();
                    }
                    window.setTimeout(() => {
                        this.successMessage = null;
                    }, 5000);
                })
                .catch((err) => {
                    if (err.response && err.response.data.errors) {
                        this.errors = err.response.data.errors;
                    }
                    this.errorMessage = "Failed to save, check for errors";
                    window.setTimeout(() => {
                        this.errorMessage = null;
                    }, 5000);
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        handleDeleteClick() {
            const confirm = window.confirm(
                "Are you sure you want to delete this event?"
            );
            if (!confirm) {
                return;
            }
            this.saving = true;
            CalendarEventsService.deleteEvent(this.form)
                .then(() => {
                    this.resetForm();
                    this.$emit("saved", null);
                })
                .catch((err) => {
                    console.error(err);
                    window.alert("Error: Failed to delete event");
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        resetForm() {
            this.form = {...initialForm};
            this.saving = false;
        },

        ensureEndAtOnOrAfterStart(start) {
            if (!window.moment.isMoment(start) || !window.moment.isMoment(this.form.end_at)) {
                return;
            }
            if (start.isAfter(this.form.end_at, 'day')) {
                this.form.end_at = start.clone().endOf('day');
            }
        },

        normalizedDates() {
            if (!isDateOnlyEventType(this.form.type)) {
                return {};
            }

            return normalizeDateOnlyRange(this.form.start_at, this.form.end_at);
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/office/calendar/Form.vue